/**
 * Top-nav project switcher: classic dropdown + in-panel search + most-recent-first ordering.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Divider,
  FormControl,
  InputAdornment,
  Menu,
  MenuItem,
  MenuList,
  OutlinedInput,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { KeyboardArrowDown as KeyboardArrowDownIcon } from '@mui/icons-material';
import type { Project } from '../services/types';

function projectRecencyMs(p: Project): number {
  const raw = p.updated_at || p.created_at || '';
  const t = raw ? Date.parse(raw) : NaN;
  return Number.isFinite(t) ? t : 0;
}

function sortProjectsByRecent(projects: Project[]): Project[] {
  return [...projects].sort((a, b) => {
    const diff = projectRecencyMs(b) - projectRecencyMs(a);
    if (diff !== 0) return diff;
    return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
  });
}

export interface ProjectSelectorProps {
  projects: Project[];
  selectedProjectId: string | null;
  onSelectProject: (projectId: string) => void;
  isLoading?: boolean;
  minWidth?: number;
}

const ProjectSelector: React.FC<ProjectSelectorProps> = ({
  projects,
  selectedProjectId,
  onSelectProject,
  isLoading = false,
  minWidth = 240,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const triggerRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [search, setSearch] = useState('');

  const open = Boolean(anchorEl);

  const sortedProjects = useMemo(() => sortProjectsByRecent(projects), [projects]);

  const selected = useMemo(
    () => sortedProjects.find((p) => p.project_id === selectedProjectId) ?? null,
    [sortedProjects, selectedProjectId],
  );

  const filteredProjects = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sortedProjects;
    return sortedProjects.filter((p) => (p.name || '').toLowerCase().includes(q));
  }, [sortedProjects, search]);

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  const handleOpen = () => {
    setAnchorEl(triggerRef.current);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePick = (projectId: string) => {
    onSelectProject(projectId);
    navigate('/dashboard');
    handleClose();
  };

  return (
    <Box sx={{ minWidth }}>
      <FormControl ref={triggerRef} size="small" sx={{ minWidth, width: '100%' }}>
        <OutlinedInput
          readOnly
          value={selected?.name ?? ''}
          placeholder="Select project"
          onClick={handleOpen}
          sx={{
            height: 36,
            cursor: 'pointer',
            bgcolor: 'background.paper',
            borderRadius: `${Number(theme.shape.borderRadius) * 0.67}px`,
            fontFamily: theme.typography.fontFamily,
            '& .MuiOutlinedInput-input': {
              fontSize: '0.875rem',
              fontWeight: 500,
              lineHeight: 1.5,
              fontFamily: theme.typography.fontFamily,
              color: theme.palette.text.primary,
              padding: '8px 14px',
            },
            '& .MuiOutlinedInput-input::placeholder': {
              color: theme.palette.text.secondary,
              opacity: 1,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'divider',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'primary.main',
            },
          }}
          endAdornment={
            <InputAdornment position="end" sx={{ mr: 0.5, color: 'text.primary' }}>
              {isLoading ? <CircularProgress color="inherit" size={16} /> : <KeyboardArrowDownIcon fontSize="small" />}
            </InputAdornment>
          }
        />
      </FormControl>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        disableAutoFocus
        PaperProps={{
          sx: {
            minWidth: triggerRef.current?.offsetWidth ?? minWidth,
            maxWidth: 360,
            borderRadius: `${Number(theme.shape.borderRadius) * 0.67}px`,
            fontFamily: theme.typography.fontFamily,
          },
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ px: 1.5, pt: 1.5, pb: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            autoFocus
            InputProps={{
              sx: {
                bgcolor: 'background.paper',
                borderRadius: `${Number(theme.shape.borderRadius) * 0.5}px`,
                fontFamily: theme.typography.fontFamily,
                '& .MuiOutlinedInput-input': {
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  lineHeight: 1.5,
                  fontFamily: theme.typography.fontFamily,
                  color: theme.palette.text.primary,
                },
                '& .MuiOutlinedInput-input::placeholder': {
                  color: theme.palette.text.secondary,
                  opacity: 1,
                },
              },
            }}
          />
        </Box>
        <Divider />
        <MenuList
          dense
          autoFocusItem={false}
          sx={{
            maxHeight: 320,
            overflow: 'auto',
            py: 0.5,
            '& .MuiMenuItem-root': {
              fontSize: '0.875rem',
              fontWeight: 500,
              lineHeight: 1.5,
              fontFamily: theme.typography.fontFamily,
              color: theme.palette.text.primary,
              minHeight: 40,
            },
            '& .MuiMenuItem-root.Mui-disabled': {
              opacity: 1,
              color: theme.palette.text.secondary,
            },
          }}
        >
          {filteredProjects.length === 0 ? (
            <MenuItem disabled>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                No matching projects
              </Typography>
            </MenuItem>
          ) : (
            filteredProjects.map((p) => (
              <MenuItem
                key={p.project_id}
                selected={p.project_id === selectedProjectId}
                onClick={() => handlePick(p.project_id)}
              >
                <Typography variant="body2" noWrap sx={{ maxWidth: 300, fontWeight: 'inherit', color: 'inherit' }}>
                  {p.name}
                </Typography>
              </MenuItem>
            ))
          )}
        </MenuList>
      </Menu>
    </Box>
  );
};

export default ProjectSelector;
